import { ScrollTrigger } from "gsap/ScrollTrigger";

// Toggles a `theme-<value>` class on `el` while its vertical centre sits
// inside a [data-navbar-theme] section. Reads live bounding rects on every
// scroll tick rather than pre-baked ScrollTrigger anchors, so it stays
// correct through fixed / sticky positioning and any transform applied to
// the element (e.g. the lifetime push's footer clamp).
//
// Pass getY to check a fixed point instead of el's own rect — e.g. a
// viewport-centered dialog whose rect is meaningless while closed/animating
// (transformed off-screen): { getY: () => window.innerHeight / 2 } tracks
// "what's behind the dialog once it's centered" continuously, so the class
// is already correct the instant it opens, no post-open re-check needed.
//
// Returns the update fn so a caller can force a re-check after moving `el`
// outside of a scroll event.
//
// Every element shares a single ScrollTrigger: each tick reads the section
// rects once (instead of once per element: nav, modals, sticky push...), then
// every element's position, and only then writes the classes, so a class
// change can't force a style recalc on the next element's reads.

const entries = new Set(); // { el, getY, active }
let sections = null;
let trigger = null;

function querySections() {
  sections = [...document.querySelectorAll("[data-navbar-theme]")].map((section) => ({
    section,
    className: `theme-${section.dataset.navbarTheme}`,
  }));
}

function yOf({ el, getY }) {
  if (getY) return getY();
  const rect = el.getBoundingClientRect();
  return rect.top + rect.height / 2;
}

function run(list) {
  // Reads first...
  const rects = sections.map(({ section }) => section.getBoundingClientRect());
  const changes = [];
  for (const entry of list) {
    const y = yOf(entry);
    const i = rects.findIndex((r) => y >= r.top && y <= r.bottom);
    const next = i === -1 ? null : sections[i].className;
    if (next !== entry.active) changes.push([entry, next]);
  }

  // ...then writes.
  for (const [entry, next] of changes) {
    if (entry.active) entry.el.classList.remove(entry.active);
    if (next) entry.el.classList.add(next);
    entry.active = next;
  }
}

export function initSectionThemeTriggers(el, { getY } = {}) {
  if (!sections) querySections();
  if (!sections.length) return () => {};

  const entry = { el, getY, active: null };
  entries.add(entry);

  if (!trigger) {
    trigger = ScrollTrigger.create({
      onUpdate: () => run(entries),
      onRefresh: () => run(entries),
    });
  }

  const update = () => run([entry]);
  update();
  return update;
}
